import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { StockBadge } from "@/components/StockBadge";
import { BrandLockup } from "@/components/BrandMark";
import { getProductPhotos } from "@/data/galleryImages";
import {
  ArrowLeft,
  Layers,
  Maximize,
  Palette,
  Ruler,
  MessageCircle,
  Send,
  ChevronRight,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const pastelMap = {
  "600-lining": "pastel-green",
  "600-gold": "pastel-yellow",
  "060-lining": "pastel-peach",
  "006-lining": "pastel-blue",
  "6000-shrinkless": "pastel-lavender",
  "super-queen": "pastel-yellow",
  "303-pure-cotton": "pastel-rose",
  "3000-pure-cotton": "pastel-mint",
  "american-crepe": "pastel-cream",
};

const productImages = {
  "600-lining": "https://5.imimg.com/data5/NSDMERP/Default/2022/7/MS/FK/DN/53158203/shrinkless-lining-cloth-1656672313469-500x500.jpg",
  "060-lining": "https://5.imimg.com/data5/NSDMERP/Default/2022/7/AA/EQ/DC/53158203/rubia-shrinkless-lining-double-dyed-cloth-1656672307696-500x500.jpg",
  "006-lining": "https://5.imimg.com/data5/NSDMERP/Default/2022/7/VR/EP/AM/53158203/rubia-shrinkless-lining-double-dyed-cloth-1656672309632-500x500.jpg",
  "6000-shrinkless": "https://5.imimg.com/data5/NSDMERP/Default/2022/7/LY/IW/GC/53158203/shrinkless-lining-cloth-44-width-1656672336809-500x500.jpg",
  "super-queen": "https://5.imimg.com/data5/JN/EY/MY-53158203/super-queen-500x500.jpg",
  "303-pure-cotton": "https://5.imimg.com/data5/CR/KI/MY-53158203/shalini-lining-500x500.jpg",
  "3000-pure-cotton": "https://5.imimg.com/data5/TB/HT/MY-53158203/lining-cloth-500x500.jpg",
  "american-crepe": "https://5.imimg.com/data5/FT/LY/MY-53158203/compliments-shrinkless-lining-fabric-500x500.jpg",
};

export default function ProductDetail() {
  const { id } = useParams();
  const [products, setProducts] = useState([]);
  const [product, setProduct] = useState(null);

  useEffect(() => {
    fetch(`${API}/products`)
      .then((res) => res.json())
      .then((data) => {
        setProducts(data);
        const found = data.find((p) => p.id === id);
        setProduct(found || null);
      })
      .catch((err) => console.error("Failed to load product:", err));
  }, [id]);

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "var(--mp-paper)" }}>
        <div className="text-center">
          <p className="text-lg mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--mp-text)" }}>
            {products.length ? "Product not found" : "Loading..."}
          </p>
          <Link to="/">
            <Button variant="outline" className="rounded-sm" data-testid="product-back-home">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const otherProducts = products.filter((p) => p.id !== id);
  const pastel = pastelMap[product.id] || "pastel-cream";
  const fabricPhotos = getProductPhotos(product.id);
  const fallbackImg = productImages[product.id];
  const backendSample = product.sample_image_url ? `${process.env.REACT_APP_BACKEND_URL}${product.sample_image_url}` : null;
  const heroImg = fabricPhotos[0]?.url || backendSample || fallbackImg;

  const specs = [
    { icon: Layers, label: "Fold", value: product.fold },
    { icon: Layers, label: "Cut Pack", value: product.cut_pack },
    { icon: Maximize, label: "Panna Type", value: product.panna },
    { icon: Ruler, label: "Width", value: product.width },
    { icon: Palette, label: "Colours Available", value: `${product.colors_available} colours` },
    { icon: Layers, label: "Dye Type", value: product.dye_type },
    { icon: Layers, label: "Material", value: product.material },
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--mp-paper)" }} data-testid="product-detail-page">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-[var(--mp-line)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between h-16 lg:h-20">
          <div className="flex items-center gap-4">
            <Link to="/" data-testid="product-back-btn">
              <Button variant="ghost" className="p-2 rounded-sm">
                <ArrowLeft className="w-5 h-5" style={{ color: "var(--mp-text-strong)" }} />
              </Button>
            </Link>
            <span
              className="text-xl font-medium"
              style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--mp-text-strong)" }}
            >
              <BrandLockup />
            </span>
          </div>
          <a href="https://wa.me/917624853840" target="_blank" rel="noopener noreferrer">
            <Button
              className="text-white text-sm font-medium px-5 py-2 rounded-sm"
              style={{ backgroundColor: "var(--mp-solid)", fontFamily: "'Manrope', sans-serif" }}
              data-testid="product-order-btn"
            >
              Order Now
            </Button>
          </a>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 lg:py-16">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 mb-8 text-sm" style={{ fontFamily: "'Manrope', sans-serif" }} data-testid="product-breadcrumb">
          <Link to="/" className="hover:underline" style={{ color: "var(--mp-muted)" }}>Home</Link>
          <ChevronRight className="w-3 h-3" style={{ color: "var(--mp-line)" }} />
          <Link to="/#products" className="hover:underline" style={{ color: "var(--mp-muted)" }}>Products</Link>
          <ChevronRight className="w-3 h-3" style={{ color: "var(--mp-line)" }} />
          <span style={{ color: "var(--mp-text-strong)" }} className="font-medium">{product.name}</span>
        </nav>

        {/* Product content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
          {/* Image */}
          <div
            className={`${pastel} border border-[var(--mp-line)] rounded-sm overflow-hidden`}
            data-testid="product-image-container"
          >
            {product.logo_url && (
              <div className="relative bg-white border-b border-[var(--mp-line)]" data-testid="product-brand-logo">
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}${product.logo_url}`}
                  alt={`${product.name} brand logo`}
                  className="w-full h-auto object-contain max-h-[280px] mx-auto p-6"
                />
                {product.badge && (
                  <span
                    className="absolute top-4 right-4 text-xs font-bold px-3 py-1.5 rounded-full shadow"
                    style={{
                      fontFamily: "'Manrope', sans-serif",
                      backgroundColor: "var(--mp-copper-solid)",
                      color: "var(--mp-text-strong)",
                      letterSpacing: "0.08em",
                    }}
                  >
                    {product.badge}
                  </span>
                )}
              </div>
            )}
            {heroImg && (
              <img
                src={heroImg}
                alt={product.name}
                className="w-full h-auto object-contain max-h-[500px] mx-auto p-4"
              />
            )}
            {fabricPhotos.length > 1 && (
              <div
                className="grid grid-cols-2 gap-2 px-4 pb-4"
                data-testid="product-image-thumbs"
              >
                {fabricPhotos.map((photo, i) => (
                  <a
                    key={photo.url}
                    href={photo.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block border border-[var(--mp-line)] rounded-sm overflow-hidden bg-white"
                    data-testid={`product-image-thumb-${i}`}
                  >
                    <img
                      src={photo.thumb}
                      alt={photo.alt}
                      className="w-full h-28 sm:h-32 object-cover"
                      loading="lazy"
                    />
                    <p
                      className="text-[10px] sm:text-xs px-2 py-1.5 truncate"
                      style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}
                    >
                      {photo.alt}
                    </p>
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div>
            <p
              className="text-xs uppercase tracking-[0.2em] font-semibold mb-2"
              style={{ color: "var(--mp-copper)", fontFamily: "'Manrope', sans-serif" }}
            >
              {product.dye_type} &bull; {product.material}
            </p>
            <h1
              className="text-3xl md:text-4xl lg:text-5xl tracking-tight leading-none font-medium mb-4"
              style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--mp-text)" }}
              data-testid="product-name"
            >
              {product.name}
            </h1>
            <div className="flex flex-wrap items-center gap-3 mb-6" data-testid="product-stock-row">
              <StockBadge status={product.stock_status || "ready"} size="md" />
              <span
                className="text-xs"
                style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-muted)" }}
              >
                {product.stock_status === "made_to_order"
                  ? "Mill dyed to your shade · 7-10 days"
                  : product.stock_status === "low"
                  ? "Limited running metres · reserve on WhatsApp"
                  : "Dispatched within 24 hours from Chickpet, Bangalore"}
              </span>
            </div>
            <p
              className="text-base leading-relaxed mb-8"
              style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-muted)" }}
            >
              {product.description}
            </p>

            {/* Colour bar (clickable → jumps to Colour Catalogue pre-filtered by this quality) */}
            <Link
              to={`/catalogue/${product.id}`}
              className="block mb-8 pastel-green border border-[var(--mp-line)] rounded-sm p-5 hover:border-[var(--mp-solid)] transition-colors group"
              data-testid="product-colours-cta"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <Palette className="w-5 h-5" style={{ color: "var(--mp-text-strong)" }} />
                  <span
                    className="text-2xl font-medium"
                    style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--mp-text-strong)" }}
                  >
                    {product.colors_available} Colours
                  </span>
                </div>
                <span
                  className="text-xs font-semibold flex items-center gap-1 opacity-70 group-hover:opacity-100"
                  style={{ color: "var(--mp-text-strong)", fontFamily: "'Manrope', sans-serif" }}
                >
                  Browse swatches <ChevronRight className="w-4 h-4" />
                </span>
              </div>
              <p className="text-sm" style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-muted)" }}>
                Available in ready stock at any given point of time — up to <strong>8,000 m per running shade</strong>. Custom colours and bulk lots dyed on request.
              </p>
              <div className="mt-3 h-2 rounded-full overflow-hidden bg-white/60">
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${Math.min(100, (product.colors_available / 446) * 100)}%`,
                    backgroundColor: "var(--mp-solid)",
                    transition: "width 1s ease-out",
                  }}
                />
              </div>
            </Link>

            {/* Specs */}
            <div className="space-y-3 mb-8" data-testid="product-specs">
              {specs.map((spec) => {
                const Icon = spec.icon;
                return (
                  <div key={spec.label} className="flex items-center justify-between py-2 border-b border-[var(--mp-line)]">
                    <div className="flex items-center gap-2">
                      <Icon className="w-4 h-4" style={{ color: "var(--mp-text-strong)" }} />
                      <span className="text-sm font-medium" style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-muted)" }}>
                        {spec.label}
                      </span>
                    </div>
                    <span className="text-sm font-semibold" style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-text)" }}>
                      {spec.value}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* American Crepe — Catalogue CTA */}
            {product.id === "american-crepe" && (
              <Link
                to="/catalogue/american-crepe"
                className="block mb-3"
                data-testid="open-crepe-catalogue-link"
              >
                <Button
                  className="w-full text-white text-sm font-medium py-6 rounded-sm"
                  style={{ backgroundColor: "var(--mp-copper-solid)", fontFamily: "'Manrope', sans-serif" }}
                >
                  <Palette className="w-5 h-5 mr-2" />
                  Browse Full Crepe Catalogue & Order by Shade #
                </Button>
              </Link>
            )}

            {/* Spec label (branded quality card) */}
            {product.label_url && (
              <div
                className="mb-8 rounded-sm overflow-hidden border border-[var(--mp-line)] bg-white"
                data-testid="product-spec-label"
              >
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}${product.label_url}`}
                  alt={`${product.name} official quality specification card`}
                  className="w-full h-auto object-contain"
                  loading="lazy"
                />
                <p
                  className="text-[11px] text-center py-2 border-t border-[var(--mp-line)]"
                  style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-muted)" }}
                >
                  Official Mutha Paras &amp; Co. specification card
                </p>
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/products-static/print/${product.id}_labels_50mm_A4.pdf`}
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                  className="block text-center text-[11px] py-2 border-t border-[var(--mp-line)] hover:bg-[var(--mp-surface)]"
                  style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-text-strong)", fontWeight: 600 }}
                  data-testid="product-download-labels"
                >
                  ⬇ Download printable A4 label sheet (50mm)
                </a>
              </div>
            )}

            {/* Lining — Catalogue CTA (for all lining products) */}
            {["600-lining", "600-gold", "060-lining", "006-lining", "6000-shrinkless", "303-pure-cotton", "3000-pure-cotton"].includes(product.id) && (
              <Link
                to="/catalogue/600-lining"
                className="block mb-3"
                data-testid="open-lining-catalogue-link"
              >
                <Button
                  className="w-full text-white text-sm font-medium py-6 rounded-sm"
                  style={{ backgroundColor: "var(--mp-copper-solid)", fontFamily: "'Manrope', sans-serif" }}
                >
                  <Palette className="w-5 h-5 mr-2" />
                  Browse Lining Charts & Order by Shade #
                </Button>
              </Link>
            )}

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-3">
              <a href="https://wa.me/917624853840" target="_blank" rel="noopener noreferrer" className="flex-1">
                <Button
                  className="w-full text-white text-sm font-medium py-6 rounded-sm"
                  style={{ backgroundColor: "#25D366", fontFamily: "'Manrope', sans-serif" }}
                  data-testid="product-whatsapp-cta"
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  Order on WhatsApp
                </Button>
              </a>
              <a href="https://t.me/+917624853840" target="_blank" rel="noopener noreferrer" className="flex-1">
                <Button
                  className="w-full text-white text-sm font-medium py-6 rounded-sm"
                  style={{ backgroundColor: "#0088cc", fontFamily: "'Manrope', sans-serif" }}
                  data-testid="product-telegram-cta"
                >
                  <Send className="w-5 h-5 mr-2" />
                  Order on Telegram
                </Button>
              </a>
            </div>
          </div>
        </div>

        {/* Related Products */}
        {otherProducts.length > 0 && (
          <div className="mt-20" data-testid="related-products">
            <h2
              className="text-2xl md:text-3xl tracking-tight font-medium mb-8"
              style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--mp-text)" }}
            >
              Other Qualities
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {otherProducts.map((p) => (
                <Link
                  key={p.id}
                  to={`/product/${p.id}`}
                  className="product-card border border-[var(--mp-line)] rounded-sm p-4 block"
                  style={{ backgroundColor: "var(--mp-card)" }}
                  data-testid={`related-product-${p.id}`}
                >
                  <img
                    src={
                      p.sample_image_url
                        ? `${process.env.REACT_APP_BACKEND_URL}${p.sample_image_url}`
                        : p.logo_url
                        ? `${process.env.REACT_APP_BACKEND_URL}${p.logo_url}`
                        : productImages[p.id] || ""
                    }
                    alt={p.name}
                    className="w-full h-28 sm:h-36 object-contain mb-3 rounded-sm"
                    loading="lazy"
                  />
                  <h4
                    className="text-base sm:text-lg font-medium mb-1"
                    style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--mp-text)" }}
                  >
                    {p.name}
                  </h4>
                  <p
                    className="text-[10px] uppercase tracking-wider font-semibold"
                    style={{ color: "var(--mp-copper)", fontFamily: "'Manrope', sans-serif" }}
                  >
                    {p.colors_available} colours
                  </p>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
